import React, { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { ListTree, Maximize2, Minimize2 } from "lucide-react";
import CollapsibleSection from "./CollapsibleSection";

/**
 * ResponseDocument - renders a COMPLETED analysis as a navigable document:
 * an outline of its own headings plus collapsible sections.
 *
 * Important constraints:
 *  - This is presentation only. The HTML is split at its existing top-level
 *    headings and re-rendered verbatim; no content is added, removed,
 *    reworded or reordered.
 *  - It is only used once streaming has finished. While a response is
 *    streaming, ResponseContent keeps its original single-blob rendering so
 *    the auto-scroll and MutationObserver behaviour is untouched.
 *  - If the document has fewer than two headings there is nothing useful to
 *    outline, so `splitIntoSections` returns null and the caller falls back
 *    to the plain rendering.
 */

const HEADING_TAGS = new Set(["h1", "h2"]);

// "I. ", "II. ", "1. ", "2." — the sequence markers the analysis prompts put on
// their top-level sections. Sub-headings inside a section ("Controlling
// Authority", "Damages Evidence") never carry one.
const NUMBERED_HEADING = /^\s*(?:[IVXLCDM]+|\d+)\s*[.)]/i;

/**
 * Which headings open a section. The converter emits every heading it finds
 * as <h1>/<h2>, so a bold sub-heading inside "III. Application" comes out at
 * the same level as the section itself and used to get its own outline chip
 * and collapsible — the bullets QA saw "listed between section titles". When
 * the document numbers its sections, only the numbered headings split it;
 * everything else stays inside the section it belongs to. Documents without
 * numbering keep splitting on every heading.
 */
function isSectionHeading(node, numberedMode) {
  const tag = node.tagName ? node.tagName.toLowerCase() : "";
  if (!HEADING_TAGS.has(tag)) return false;
  if (!numberedMode) return true;
  return NUMBERED_HEADING.test(node.textContent || "");
}

export function splitIntoSections(html) {
  if (!html || typeof window === "undefined" || typeof DOMParser === "undefined") {
    return null;
  }

  let root;
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="rs-root">${html}</div>`,
      "text/html",
    );
    root = doc.getElementById("rs-root");
  } catch (err) {
    return null;
  }
  if (!root) return null;

  const preamble = [];
  const sections = [];
  let current = null;

  const children = Array.from(root.children);
  const numberedCount = children.filter(
    (node) =>
      HEADING_TAGS.has(node.tagName ? node.tagName.toLowerCase() : "") &&
      NUMBERED_HEADING.test(node.textContent || ""),
  ).length;
  const numberedMode = numberedCount >= 2;

  children.forEach((node) => {
    if (isSectionHeading(node, numberedMode)) {
      if (current) sections.push(current);
      current = { title: (node.textContent || "").trim(), html: "" };
    } else if (current) {
      current.html += node.outerHTML;
    } else {
      preamble.push(node.outerHTML);
    }
  });
  if (current) sections.push(current);

  // Not enough structure to be worth outlining.
  if (sections.length < 2) return null;

  return { preamble: preamble.join(""), sections };
}

/**
 * SectionedHtml - renders an HTML blob as a navigable sectioned document when
 * it has enough structure, and as plain HTML otherwise.
 *
 * Used by the Comparison and Synthesis stages, which previously rendered raw
 * HTML into a fixed-height scroll box and so never got the outline or the
 * collapsible sections that the per-model analyses have.
 *
 * `isLoading` must be passed through: while streaming we keep the plain blob
 * so the existing auto-scroll / MutationObserver logic in Dashboard.js keeps
 * observing a stable node, exactly as ResponseContent does.
 */
export const SectionedHtml = forwardRef(function SectionedHtml(
  { html, isLoading = false, modelName = "", ...rest },
  ref,
) {
  const parsed = useMemo(() => {
    if (isLoading || !html || !html.trim()) return null;
    return splitIntoSections(html);
  }, [html, isLoading]);

  // forwardRef is required: Dashboard.js attaches comparisonResponseRef /
  // synthesisResponseRef here and drives auto-scroll off the resulting node.
  if (parsed) {
    return (
      <div ref={ref} {...rest}>
        <ResponseDocument parsed={parsed} modelName={modelName} />
      </div>
    );
  }
  return <div ref={ref} {...rest} dangerouslySetInnerHTML={{ __html: html }} />;
});

export default function ResponseDocument({ parsed, modelName = "" }) {
  const { preamble, sections } = parsed;

  // Only the first section is open on load; the rest start collapsed so the
  // document opens as a scannable outline instead of a wall of text.
  const [collapsed, setCollapsed] = useState(() => sections.map((_, i) => i !== 0));
  const sectionRefs = useRef([]);

  const allCollapsed = useMemo(
    () => collapsed.every(Boolean),
    [collapsed],
  );

  const toggleAll = useCallback(() => {
    setCollapsed((prev) => prev.map(() => !prev.every(Boolean)));
  }, []);

  const setOne = useCallback((index, open) => {
    setCollapsed((prev) => {
      const next = [...prev];
      next[index] = !open;
      return next;
    });
  }, []);

  const jumpTo = useCallback((index) => {
    setCollapsed((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      next[index] = false;
      return next;
    });
    // Let the panel start expanding before scrolling to it.
    requestAnimationFrame(() => {
      const el = sectionRefs.current[index];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, []);

  return (
    <div className="rs-document">
      <div className="rs-outline">
        <div className="rs-outline-head">
          <ListTree size={14} />
          <span>Sections</span>
          <button
            type="button"
            className="rs-outline-toggle"
            onClick={toggleAll}
            aria-label={allCollapsed ? "Expand all sections" : "Collapse all sections"}
          >
            {allCollapsed ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        </div>

        <div className="rs-outline-chips">
          {sections.map((section, i) => (
            <button
              key={`${section.title}-${i}`}
              type="button"
              className="rs-outline-chip"
              onClick={() => jumpTo(i)}
              title={section.title}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {preamble && (
        <div
          className="rs-document-preamble"
          dangerouslySetInnerHTML={{ __html: preamble }}
        />
      )}

      <div className="rs-document-sections">
        {sections.map((section, i) => (
          <div
            key={`${section.title}-${i}`}
            ref={(el) => {
              sectionRefs.current[i] = el;
            }}
          >
            <CollapsibleSection
              title={section.title}
              open={!collapsed[i]}
              onToggle={(open) => setOne(i, open)}
              className="rs-document-section"
            >
              <div dangerouslySetInnerHTML={{ __html: section.html }} />
            </CollapsibleSection>
          </div>
        ))}
      </div>

      <span className="ds-sr-only">
        {modelName} analysis, {sections.length} sections
      </span>
    </div>
  );
}
